import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Home, MessageSquare, FolderOpen, UserCircle, Languages, Image as ImageIcon,
  Activity, Users, Mail, FileText, Settings, Headphones, FileSearch,
  Play, Pause, Clock, MoreHorizontal, X,
} from 'lucide-react'
import './bunker-tour.css'
import { useScenario } from './bunkerDemoScenario'
import { BunkerMark } from './demo-screens/BunkerMark'
import { StartScreen, type TileTarget } from './demo-screens/StartScreen'
import { ChatScreen } from './demo-screens/ChatScreen'
import { DocumentsScreen } from './demo-screens/DocumentsScreen'
import { MonitoringScreen } from './demo-screens/MonitoringScreen'
import { TranslateScreen } from './demo-screens/TranslateScreen'
import { ImageGenScreen } from './demo-screens/ImageGenScreen'
import { KIProfileScreen } from './demo-screens/KIProfileScreen'
import { UsersScreen } from './demo-screens/UsersScreen'
import { MeetingScreen } from './demo-screens/MeetingScreen'
import { ProposalScreen } from './demo-screens/ProposalScreen'
import { EmailScreen } from './demo-screens/EmailScreen'
import { DocAnalysisScreen } from './demo-screens/DocAnalysisScreen'
import { SettingsScreen } from './demo-screens/SettingsScreen'

/**
 * BunkerDemoTour — a guided, multi-screen product tour that reproduces the real
 * bunker.ai app: a persistent sidebar menu over a dark "app window", with an
 * autoplay highlight reel through the features and a "Selbst klicken" mode where
 * the visitor navigates via the sidebar. Each screen is a faithful, decoupled
 * reproduction; new features are a new screen + nav entry.
 */

type Mode = 'autoplay' | 'interactive'
type LucideIcon = typeof Home

interface NavItem {
  id: string
  icon: LucideIcon
  /** Preview-only ("Kommt bald") feature — excluded from the autoplay reel. */
  soon?: boolean
}

/**
 * Nav structure (id + icon + soon). The visible label is resolved at render
 * from the `demo` namespace via `td('tour.nav.<id>')`.
 */
const NAV: readonly NavItem[] = [
  { id: 'home', icon: Home },
  { id: 'chat', icon: MessageSquare },
  { id: 'documents', icon: FolderOpen },
  { id: 'briefpost', icon: Mail },
  { id: 'angebote', icon: FileText },
  { id: 'gespraeche', icon: Headphones },
  { id: 'doc-intelligence', icon: FileSearch, soon: true },
  { id: 'translate', icon: Languages },
  { id: 'image', icon: ImageIcon },
  { id: 'profile', icon: UserCircle },
  { id: 'monitoring', icon: Activity },
  { id: 'users', icon: Users },
  { id: 'settings', icon: Settings },
]

/**
 * Phone layout: the desktop sidebar rail is replaced by a bottom tab bar with a
 * few PRIMARY tabs + a "Mehr" button that opens a slide-up sheet with the rest —
 * exactly how the real bunker.ai app behaves on phones (see custom-rag
 * MobileBottomNav). Anything not primary lives in the "Mehr" sheet.
 */
const MOBILE_PRIMARY: readonly string[] = ['home', 'chat', 'briefpost', 'angebote']

/** Autoplay highlight reel (preview-only items are excluded). */
const TOUR: readonly string[] = [
  'home', 'chat', 'briefpost', 'angebote', 'gespraeche', 'documents', 'monitoring', 'translate', 'image', 'profile', 'users', 'settings',
]

/** Dwell time per screen in autoplay (ms). 'chat' advances via its onComplete. */
const DWELL: Record<string, number> = {
  home: 3800, briefpost: 6400, angebote: 6800, gespraeche: 6800,
  documents: 5200, monitoring: 5200, translate: 5000, image: 5000, profile: 5200, users: 4600, settings: 5600,
}

export interface BunkerDemoTourProps {
  className?: string
  /** Fill the parent's height (full-page use) instead of a fixed window height. */
  fillHeight?: boolean
}

export function BunkerDemoTour({ className, fillHeight }: BunkerDemoTourProps) {
  const { t } = useTranslation('home')
  // Demo-namespace chrome (nav labels, soon suffix, titlebar/fn fallbacks).
  const { t: td } = useTranslation('demo')
  // The product tour autoplays and loops by default. A segmented control lets
  // the visitor switch between "Autoplay" (auto-advancing reel) and "Selbst
  // klicken" (free navigation via the sidebar / tiles). In autoplay a
  // Play/Pause button is available; a manual nav click also pauses the loop.
  const [mode, setMode] = useState<Mode>('autoplay')
  const [playing, setPlaying] = useState(true)

  const enterMode = (next: Mode) => {
    setMode(next)
    setPlaying(next === 'autoplay')
  }
  const [activeId, setActiveId] = useState<string>('home')
  // Which preview variant the Dok.-Analyse screen shows: 'contractCheck'
  // (Dokumente prüfen) or 'docAnalysis' (Dokumente analysieren). Set by the
  // StartScreen tiles; the other use cases route 1:1 to a dedicated screen.
  const [docScenarioId, setDocScenarioId] = useState<string>('contractCheck')
  // Phone-only "Mehr" sheet (secondary nav). No effect on desktop, where the
  // full sidebar rail is shown instead.
  const [moreOpen, setMoreOpen] = useState(false)
  const timerRef = useRef<number | null>(null)

  // Translated nav label for a screen id (and the titlebar title for it).
  const navLabel = (id: string): string => td(`tour.nav.${id}`)

  const tourPos = TOUR.indexOf(activeId)

  // The Chat screen always plays the knowledge (RAG) walkthrough; the other use
  // cases each have their own faithful product screen.
  const knowledgeScenario = useScenario('knowledge')

  const advance = useCallback(() => {
    setActiveId((current) => {
      const i = TOUR.indexOf(current)
      const next = i < 0 ? 0 : (i + 1) % TOUR.length
      return TOUR[next]
    })
  }, [])

  // Autoplay: dwell on each tour screen, then advance. 'chat' is driven by its
  // own onComplete (the chat flow takes a variable amount of time).
  useEffect(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (mode !== 'autoplay' || !playing || activeId === 'chat') return
    if (TOUR.indexOf(activeId) < 0) return // a "soon" preview screen — don't auto-advance
    const dwell = DWELL[activeId] ?? 4800
    timerRef.current = window.setTimeout(advance, dwell)
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [mode, playing, activeId, advance])

  const handleChatComplete = useCallback(() => {
    if (mode === 'autoplay' && playing) {
      window.setTimeout(advance, 1400)
    }
  }, [mode, playing, advance])

  // Close the "Mehr" sheet on Escape (matches the real app's behaviour).
  useEffect(() => {
    if (!moreOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [moreOpen])

  const onNavClick = (id: string) => {
    if (mode === 'autoplay') return
    setActiveId(id)
    setMoreOpen(false)
  }

  // Phone secondary items = everything not on the bottom tab bar.
  const secondaryNav = NAV.filter((n) => !MOBILE_PRIMARY.includes(n.id))
  // Highlight "Mehr" when the active screen lives in the secondary sheet.
  const isSecondaryActive = secondaryNav.some((n) => n.id === activeId)

  // A StartScreen tile opens the real product screen for that use case. The two
  // Dok.-Analyse previews share one screen, disambiguated by scenarioId.
  const onTileClick = (target: TileTarget) => {
    if (mode === 'autoplay') return
    if (target.scenarioId) setDocScenarioId(target.scenarioId)
    setActiveId(target.id)
  }

  function renderScreen() {
    switch (activeId) {
      case 'home':
        return <StartScreen onTileClick={onTileClick} />
      case 'chat':
        return (
          <ChatScreen
            active
            mode={mode}
            scenario={knowledgeScenario}
            onComplete={handleChatComplete}
          />
        )
      case 'briefpost':
        return <EmailScreen active scenarioId="customer" />
      case 'angebote':
        return <ProposalScreen active scenarioId="proposal" />
      case 'gespraeche':
        return <MeetingScreen active scenarioId="meeting" />
      case 'doc-intelligence':
        return (
          <DocAnalysisScreen
            active
            scenarioId={docScenarioId === 'docAnalysis' ? 'docAnalysis' : 'contractCheck'}
          />
        )
      case 'documents':
        return <DocumentsScreen />
      case 'monitoring':
        return <MonitoringScreen />
      case 'translate':
        return <TranslateScreen />
      case 'image':
        return <ImageGenScreen />
      case 'profile':
        return <KIProfileScreen />
      case 'users':
        return <UsersScreen />
      case 'settings':
        return <SettingsScreen />
      default: {
        const item = NAV.find((n) => n.id === activeId)
        const Icon = item?.icon ?? Clock
        return (
          <div className="bnk-tour__soon">
            <div className="bnk-tour__soon-icon"><Icon size={28} aria-hidden="true" /></div>
            <h3>{item ? navLabel(item.id) : td('tour.fnFallback')}</h3>
            <span className="badge">{t('productTour.soonTitle')}</span>
            <p>{t('productTour.soonBody')}</p>
          </div>
        )
      }
    }
  }

  return (
    <div className={['bnk-tour', fillHeight ? 'bnk-tour--fill' : '', className].filter(Boolean).join(' ')}>
      <div className="bnk-tour__window">
        <div className="bnk-tour__titlebar">
          <div className="bnk-tour__dots" aria-hidden="true"><i /><i /><i /></div>
          <span>bunker.ai · <b>{NAV.some((n) => n.id === activeId) ? navLabel(activeId) : td('tour.titlebarFallback')}</b></span>
        </div>

        <div className="bnk-tour__body">
          <nav className="bnk-tour__rail" aria-label="bunker.ai Navigation" data-locked={mode === 'autoplay' ? '' : undefined}>
            <div className="bnk-tour__logo"><BunkerMark size={26} title="bunker.ai" /></div>
            {NAV.map((item) => {
              const Icon = item.icon
              const isActive = activeId === item.id
              const label = navLabel(item.id)
              return (
                <button
                  key={item.id}
                  type="button"
                  className="bnk-tour__navbtn"
                  data-active={isActive}
                  data-soon={item.soon ? '' : undefined}
                  aria-label={label}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => onNavClick(item.id)}
                >
                  <Icon size={20} aria-hidden="true" />
                  <span className="bnk-tour__tip">{label}{item.soon ? td('tour.soonSuffix') : ''}</span>
                </button>
              )
            })}
          </nav>

          <div className="bnk-tour__content" data-autoplay={mode === 'autoplay' ? '' : undefined}>{renderScreen()}</div>
        </div>

        {/* Phone-only bottom tab bar: primary tabs + "Mehr" (matches the real
            bunker.ai app, which swaps the sidebar for a bottom nav on phones). */}
        <nav className="bnk-tour__tabbar" aria-label="bunker.ai Navigation" data-locked={mode === 'autoplay' ? '' : undefined}>
          {MOBILE_PRIMARY.map((id) => {
            const item = NAV.find((n) => n.id === id)
            if (!item) return null
            const Icon = item.icon
            const isActive = activeId === id
            return (
              <button
                key={id}
                type="button"
                className="bnk-tour__tab"
                data-active={isActive}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => onNavClick(id)}
              >
                <Icon size={20} aria-hidden="true" />
                <span>{navLabel(id)}</span>
              </button>
            )
          })}
          <button
            type="button"
            className="bnk-tour__tab"
            data-active={moreOpen || isSecondaryActive}
            aria-expanded={moreOpen}
            aria-label={td('tour.more')}
            onClick={() => setMoreOpen((o) => !o)}
          >
            <MoreHorizontal size={20} aria-hidden="true" />
            <span>{td('tour.more')}</span>
          </button>
        </nav>

        {/* "Mehr" slide-up sheet with the remaining screens (phone only). */}
        {moreOpen && (
          <>
            <button
              type="button"
              className="bnk-tour__sheet-backdrop"
              aria-label={td('tour.close')}
              onClick={() => setMoreOpen(false)}
            />
            <div className="bnk-tour__sheet" role="dialog" aria-label={td('tour.more')}>
              <div className="bnk-tour__sheet-head">
                <span>{td('tour.more')}</span>
                <button
                  type="button"
                  className="bnk-tour__sheet-close"
                  aria-label={td('tour.close')}
                  onClick={() => setMoreOpen(false)}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
              <div className="bnk-tour__sheet-grid">
                {secondaryNav.map((item) => {
                  const Icon = item.icon
                  const isActive = activeId === item.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="bnk-tour__sheet-item"
                      data-active={isActive}
                      data-soon={item.soon ? '' : undefined}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => onNavClick(item.id)}
                    >
                      <Icon size={20} aria-hidden="true" />
                      <span>{navLabel(item.id)}{item.soon ? td('tour.soonSuffix') : ''}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="bnk-tour__controls">
        <button type="button" className="bnk-tour__ctrlbtn" onClick={() => setPlaying((p) => !p)} disabled={mode !== 'autoplay'}>
          {playing && mode === 'autoplay' ? <Pause size={15} aria-hidden="true" /> : <Play size={15} aria-hidden="true" />}
          {playing && mode === 'autoplay' ? t('productTour.pause') : t('productTour.play')}
        </button>
        <button
          type="button"
          className="bnk-tour__autoplay-toggle"
          onClick={() => enterMode(mode === 'autoplay' ? 'interactive' : 'autoplay')}
          aria-pressed={mode === 'autoplay'}
          aria-label={t('productTour.modeLabel')}
        >
          <span className="bnk-tour__toggle-track" data-on={mode === 'autoplay'}>
            <span className="bnk-tour__toggle-thumb" />
          </span>
          <span className="bnk-tour__toggle-label">
            {mode === 'autoplay' ? t('productTour.autoplay') : t('productTour.interactive')}
          </span>
        </button>
        <div className="bnk-tour__progress" aria-hidden="true">
          {TOUR.map((id, i) => (
            <i key={id} data-on={i === tourPos} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default BunkerDemoTour
